import React from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const ServiceModal = ({ service, onClose }) => {
  if (!service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {service?.title}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-muted hover:bg-destructive hover:text-destructive-foreground transition-all duration-300 flex items-center justify-center"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <div className="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden mb-6">
            <Image
              src={service?.image}
              alt={service?.imageAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-6 left-6 flex items-center gap-4">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                <Icon name={service?.icon} size={32} color="#ffffff" />
              </div>
              <div>
                <div className="text-white/80 text-sm mb-1">
                  {service?.category}
                </div>
                <div className="text-white text-2xl font-bold">
                  {service?.title}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-success/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name="TrendingUp"
                  size={20}
                  color="var(--color-success)"
                />
                <span className="text-sm font-semibold text-success">
                  ROI Impact
                </span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {service?.roi}
              </div>
            </div>
            <div className="bg-primary/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={20} color="var(--color-primary)" />
                <span className="text-sm font-semibold text-primary">
                  Implementation
                </span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {service?.timeline}
              </div>
            </div>
            <div className="bg-warning/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Users" size={20} color="var(--color-warning)" />
                <span className="text-sm font-semibold text-warning">
                  Clients Served
                </span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {service?.clientCount}+
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-4">
              Service Overview
            </h3>
            <p className="text-sm md:text-base text-text-secondary leading-relaxed">
              {service?.fullDescription}
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-4">
              Key Features & Capabilities
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {service?.features?.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-muted rounded-lg p-3"
                >
                  <Icon
                    name="CheckCircle2"
                    size={20}
                    color="var(--color-success)"
                    className="mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-text-secondary">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-4">
              Technology Stack
            </h3>
            <div className="flex flex-wrap gap-2">
              {service?.technologies?.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs md:text-sm font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-4">
              Success Story
            </h3>
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-4 md:p-6 border border-primary/20">
              <div className="flex items-start gap-4 mb-4">
                <Image
                  src={service?.caseStudy?.clientLogo}
                  alt={service?.caseStudy?.clientLogoAlt}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <div className="font-bold text-foreground">
                    {service?.caseStudy?.clientName}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {service?.caseStudy?.industry}
                  </div>
                </div>
              </div>
              <p className="text-sm md:text-base text-text-secondary mb-4">
                {service?.caseStudy?.challenge}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {service?.caseStudy?.results?.map((result, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                      {result?.value}
                    </div>
                    <div className="text-xs md:text-sm text-text-secondary">
                      {result?.metric}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button className="flex-1 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2">
              <Icon name="Calendar" size={20} />
              Schedule Consultation
            </button>
            <button className="flex-1 bg-secondary text-secondary-foreground px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2">
              <Icon name="FileText" size={20} />
              Download Brochure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
